import { supabase } from '../lib/supabase';
import { uploadToStorage } from './imageService';
const categoryCache = new Map();




export const wordpressService = {

  // Mark article as "generating" / queue for processing
  queueArticle: async (articleId) => {
    if (!articleId) return null;

    // 🚨 Adjust this to match how you talk to Supabase / your API.
    // If you already use Supabase client inside this file, reuse it.
    const { data, error } = await supabase?.from('wp_articles')?.update({ status: 'generating' })?.eq('id', articleId)?.select()?.single();

    if (error) {
      console.error('Error queueing article:', error);
      throw error;
    }

    return data;
  },



updateSiteStatus: async (siteId, payload) => {
  if (!siteId) throw new Error('Missing siteId');

  const { error } = await supabase?.from('wp_sites')?.update(payload)?.eq('id', siteId);

  if (error) {
    console.error('Failed to update site status:', error);
    throw error;
  }

  return true;
},



  /**
   * Get all WordPress sites for the current user
   */
  async getAllSites(profileId = null) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();

      if (authError) {
        console.error('Authentication error:', authError);
        throw new Error('Authentication failed. Please sign in again.');
      }

      if (!user) {
        throw new Error('Not authenticated. Please sign in to continue.');
      }

      let query = supabase?.from('wp_sites')?.select('*')?.eq('user_id', user?.id);
      if (profileId) query = query?.eq('profile_id', profileId);
      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(site => ({
        id: site?.id,
        userId: site?.user_id,
        profileId: site?.profile_id,
        siteName: site?.site_name,
        siteUrl: site?.site_url,
        wpUsername: site?.wp_username,
        wpAppPassword: site?.wp_app_password,
        defaultCategory: site?.default_category,
        status: site?.status,
        lastSync: site?.last_sync,
        createdAt: site?.created_at,
        updatedAt: site?.updated_at
      }));
    } catch (error) {
      console.error('Error in getAllSites:', error);
      throw error;
    }
  },

  /**
   * Create a new WordPress site
   */
  async createSite(siteData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('wp_sites')?.insert({
        user_id: user?.id,
        profile_id: siteData?.profileId || null,
        site_name: siteData?.siteName,
        site_url: siteData?.siteUrl,
        wp_username: siteData?.wpUsername,
        wp_app_password: siteData?.wpAppPassword,
        default_category: siteData?.defaultCategory || null,
        status: 'unknown'
      })?.select()?.single();

    if (error) throw error;

    // Convert back to camelCase
    return {
      id: data?.id,
      userId: data?.user_id,
      profileId: data?.profile_id,
      siteName: data?.site_name,
      siteUrl: data?.site_url,
      wpUsername: data?.wp_username,
      wpAppPassword: data?.wp_app_password,
      defaultCategory: data?.default_category,
      status: data?.status,
      lastSync: data?.last_sync,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Update an existing WordPress site
   */
  async updateSite(siteId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {};
    if (updates?.siteName !== undefined) updateData.site_name = updates?.siteName;
    if (updates?.siteUrl !== undefined) updateData.site_url = updates?.siteUrl;
    if (updates?.wpUsername !== undefined) updateData.wp_username = updates?.wpUsername;
    if (updates?.wpAppPassword !== undefined) updateData.wp_app_password = updates?.wpAppPassword;
    if (updates?.defaultCategory !== undefined) updateData.default_category = updates?.defaultCategory;
    if (updates?.status !== undefined) updateData.status = updates?.status;
    if (updates?.lastSync !== undefined) updateData.last_sync = updates?.lastSync;
    if (updates?.wpToken !== undefined) updateData.wp_token = updates?.wpToken;

    const { data, error } = await supabase?.from('wp_sites')?.update(updateData)?.eq('id', siteId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      siteName: data?.site_name,
      siteUrl: data?.site_url,
      wpUsername: data?.wp_username,
      wpAppPassword: data?.wp_app_password,
      defaultCategory: data?.default_category,
      status: data?.status,
      lastSync: data?.last_sync,
      wpToken: data?.wp_token,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Delete a WordPress site
   */
  async deleteSite(siteId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('wp_sites')?.delete()?.eq('id', siteId)?.eq('user_id', user?.id);

    if (error) throw error;
  },

  /**
   * Test connection to the WordPress GymPlix plugin.
   * Steps:
   * 1) Health
   * 2) Auth
   * 3) Categories
   */
  async testGymPlixConnection(siteId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get site row
    const { data: site, error } = await supabase?.from('wp_sites')?.select('*')?.eq('id', siteId)?.eq('user_id', user?.id)?.single();

    if (error) {
      throw new Error('Failed loading WordPress site info: ' + error.message);
    }

    const base = site?.site_url?.replace(/\/$/, '');
    const username = site?.wp_username;
    const password = site?.wp_app_password;

    // ----------------------------------------------
    // 1) Health check
    // ----------------------------------------------
    let health;
    try {
      const res = await fetch(`${base}/wp-json/gymplix/v1/test`);
      health = await res?.json();
    } catch (err) {
      throw new Error('Cannot reach WordPress: ' + err.message);
    }

    if (!health?.success) {
      return {
        success: false,
        healthCheck: false,
        statusBadge: '🔴 Plugin not reachable',
        message: 'GymPlix plugin test endpoint failed'
      };
    }

    // ----------------------------------------------
    // 2) Token auth
    // ----------------------------------------------
    let auth;
    try {
      const res = await fetch(`${base}/wp-json/gymplix/v1/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      auth = await res?.json();
    } catch (err) {
      return {
        success: false,
        healthCheck: true,
        auth: false,
        statusBadge: '🔴 Auth error',
        message: 'Cannot authenticate with WordPress: ' + err?.message
      };
    }

    if (!auth?.success || !auth?.token) {
      return {
        success: false,
        healthCheck: true,
        auth: false,
        statusBadge: '🔴 Authentication failed',
        message: 'Invalid username or password'
      };
    }

    // Save token in wp_sites
    await supabase?.from('wp_sites')?.update({ wp_token: auth?.token })?.eq('id', siteId);

    // ----------------------------------------------
    // 3) Categories
    // ----------------------------------------------
    let categories;
    try {
      const res = await fetch(`${base}/wp-json/gymplix/v1/categories`, {
        headers: {
          Authorization: `Bearer ${auth?.token}`
        }
      });
      categories = await res?.json();
    } catch (err) {
      return {
        success: false,
        healthCheck: true,
        auth: true,
        categories: false,
        statusBadge: '🟡 Categories error',
        message: 'Could not fetch categories: ' + err?.message
      };
    }

    if (!categories?.success) {
      return {
        success: false,
        healthCheck: true,
        auth: true,
        categories: false,
        statusBadge: '🟡 Categories error',
        message: categories?.message || 'Category fetch failed'
      };
    }

    // ----------------------------------------------
    // All good 🎉
    // ----------------------------------------------
    return {
      success: true,
      healthCheck: true,
      auth: true,
      categories: true,
      statusBadge: '🟢 Connected',
      message: 'GymPlix fully operational'
    };
  },

  /**
   * Fetch categories using GymPlix Connector v2 plugin
   * Uses stored token or generates new one if authentication fails
   * NOW WITH: Automatic token refresh, background verification, and caching
   */
  async fetchGymPlixCategories(siteId, options = {}) {
    const { skipCache = false, backgroundRefresh = false } = options;
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check cache first (5 minute TTL)
    const cacheKey = `wp_categories_${siteId}`;
    const cached = categoryCache?.get(cacheKey);
    if (!skipCache && cached && (Date.now() - cached?.timestamp < 5 * 60 * 1000)) {
      // Return cached data immediately, optionally refresh in background
      if (backgroundRefresh) {
        wordpressService?.fetchGymPlixCategories(siteId, { skipCache: true })?.catch(err => 
          console.warn('Background category refresh failed:', err)
        );
      }
      return cached?.data;
    }

    // Get site details including stored token
    const { data: site, error: siteError } = await supabase?.from('wp_sites')?.select('*')?.eq('id', siteId)?.eq('user_id', user?.id)?.single();

    if (siteError) throw siteError;
    if (!site) throw new Error('Site not found');

    try {
      let token = site?.wp_token;
      let tokenRefreshed = false;
      
      // Helper function to authenticate and get new token
      const authenticateAndGetToken = async () => {
        try {
          const authResponse = await fetch(`${site?.site_url}/wp-json/gymplix/v1/token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              username: site?.wp_username,
              password: site?.wp_app_password
            })
          });

          const authData = await authResponse?.json();

          if (!authData?.success || !authData?.token) {
            return null;
          }

          const newToken = authData?.token;
          
          // Store new token in database
          await wordpressService?.updateSite(siteId, {
            wpToken: newToken,
            status: 'connected'
          });

          return newToken;
        } catch (authError) {
          console.error('Authentication error:', authError);
          return null;
        }
      };

      // If no token stored, attempt to authenticate
      if (!token) {
        token = await authenticateAndGetToken();
        tokenRefreshed = true;
        
        if (!token) {
          const errorResult = {
            success: false,
            error: '🔴 Authentication failed. Please update username and password.',
            categories: [],
            isAuthError: true,
            requiresReauth: true
          };
          // Cache error state briefly (30 seconds)
          categoryCache?.set(cacheKey, { data: errorResult, timestamp: Date.now() - 4.5 * 60 * 1000 });
          return errorResult;
        }
      }

      // Attempt to fetch categories with stored/new token
      try {
        const categoriesResponse = await fetch(`${site?.site_url}/wp-json/gymplix/v1/categories`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const categoriesData = await categoriesResponse?.json();

        // Check if token expired or invalid
        if (categoriesResponse?.status === 401 || categoriesResponse?.status === 403) {
          // Only retry once if we haven't already refreshed the token
          if (!tokenRefreshed) {
            token = await authenticateAndGetToken();
            
            if (token) {
              // Retry with new token
              const retryResponse = await fetch(`${site?.site_url}/wp-json/gymplix/v1/categories`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              const retryData = await retryResponse?.json();
              
              if (retryData?.success && Array.isArray(retryData?.categories)) {
                const result = {
                  success: true,
                  categories: retryData?.categories?.map(cat => ({
                    id: cat?.id,
                    name: cat?.name
                  })),
                  error: null
                };
                // Cache successful result
                categoryCache?.set(cacheKey, { data: result, timestamp: Date.now() });
                return result;
              }
            }
          }
          
          // Clear invalid token
          await wordpressService?.updateSite(siteId, {
            wpToken: null,
            status: 'disconnected'
          });
          
          const errorResult = {
            success: false,
            error: '🔴 Authentication expired. Please reconnect your site.',
            categories: [],
            isAuthError: true,
            requiresReauth: true
          };
          // Cache error state briefly
          categoryCache?.set(cacheKey, { data: errorResult, timestamp: Date.now() - 4.5 * 60 * 1000 });
          return errorResult;
        }

        // Check if response has success and categories array
        if (!categoriesData?.success || !Array.isArray(categoriesData?.categories)) {
          const errorResult = {
            success: false,
            error: '🔴 GymPlix Connector v2 cannot fetch categories - check plugin configuration',
            categories: [],
            isCategoriesError: true
          };
          // Cache error state briefly
          categoryCache?.set(cacheKey, { data: errorResult, timestamp: Date.now() - 4.5 * 60 * 1000 });
          return errorResult;
        }

        // Return categories in the expected format { id, name }
        const result = {
          success: true,
          categories: categoriesData?.categories?.map(cat => ({
            id: cat?.id,
            name: cat?.name
          })),
          error: null
        };
        
        // Cache successful result
        categoryCache?.set(cacheKey, { data: result, timestamp: Date.now() });
        
        // Update site status to connected
        await wordpressService?.updateSite(siteId, { status: 'connected' });
        
        return result;
      } catch (fetchError) {
        const errorResult = {
          success: false,
          error: `🔴 Cannot fetch categories: ${fetchError?.message || 'Network error'}`,
          categories: [],
          isCategoriesError: true
        };
        // Cache error state briefly
        categoryCache?.set(cacheKey, { data: errorResult, timestamp: Date.now() - 4.5 * 60 * 1000 });
        return errorResult;
      }
    } catch (error) {
      console.error('Error fetching GymPlix categories:', error);
      const errorResult = {
        success: false,
        error: error?.message || 'Failed to fetch categories from GymPlix Connector',
        categories: []
      };
      // Cache error state briefly
      categoryCache?.set(cacheKey, { data: errorResult, timestamp: Date.now() - 4.5 * 60 * 1000 });
      return errorResult;
    }
  },

  /**
   * Clear category cache for a specific site or all sites
   */
clearCategoryCache(siteId = null) {
  if (siteId) {
    categoryCache?.delete(`wp_categories_${siteId}`);
  } else {
    categoryCache?.clear();
  }
},

  /**
   * Preload categories in background for faster subsequent access
   */
  preloadCategories(siteId) {
    if (!siteId) return;
    // Fetch categories in background without blocking
    this.fetchGymPlixCategories(siteId, { skipCache: false, backgroundRefresh: true })?.catch(err => console.warn('Background category preload failed:', err));
  },

  /**
   * DISABLED: Images are no longer uploaded to WordPress Media Library.
   * All images live exclusively on storage.gymplix.com.
   * This function is kept for backward compatibility but does NOT upload anything.
   * Pass a storageUrl to get it echoed back in the expected shape.
   */
  async uploadImageToWordPress(siteId, imageFile, metadata) {
    // If a pre-existing storage URL was attached to the file object, return it directly.
    const storageUrl = imageFile?.storageUrl || null;
    console.log('ℹ️ uploadImageToWordPress: WordPress media upload is disabled. Using storage URL:', storageUrl);
    return {
      wp_media_id: null,
      wp_url: storageUrl,
      title: metadata?.title || '',
      alt_text: metadata?.alt_text || '',
      caption: metadata?.caption || '',
      description: metadata?.description || ''
    };
  },

  /**
   * Generate AI images and upload to storage.gymplix.com ONLY.
   * WordPress media library is never touched.
   * Returns storage URLs that are embedded directly into post content / meta.
   */
  async generateAndUploadArticleImages(siteId, focusKeyword, imageLocations = ['hero']) {
    try {
      const uploadedImages = [];

      for (const location of imageLocations) {
        console.log(`🎨 Generating image for ${location}: ${focusKeyword}`);

        const imageMetadata = {
          title: `${focusKeyword} - ${location}`,
          alt_text: `Professional ${focusKeyword} image`,
          caption: `${focusKeyword}`,
          description: `High-quality ${focusKeyword} photography`
        };

        // Generate AI image blob (placeholder — replace with real MidJourney call)
        const aiImageBlob = await this.generateAIImagePlaceholder(focusKeyword, location);

        // Upload to storage.gymplix.com via storage-upload.php
        const storageUrl = await this.uploadImageToGymplixStorage(aiImageBlob, focusKeyword, location);

        uploadedImages?.push({
          location,
          wp_media_id: null,       // No WordPress media ID — we don't upload to WP
          wp_url: storageUrl,      // URL on storage.gymplix.com
          storage_url: storageUrl,
          alt_text: imageMetadata?.alt_text
        });

        console.log(`✅ Image stored on storage.gymplix.com: ${storageUrl}`);
      }

      return uploadedImages;
    } catch (error) {
      console.error('Error generating article images:', error);
      throw new Error(`Failed to generate images: ${error?.message}`);
    }
  },

  /**
   * Upload an image blob to Cloudflare R2.
   * Returns the public R2 URL.
   */
  async uploadImageToGymplixStorage(imageBlob, keyword, location) {
    try {
      const filename = `${location}-${Date.now()}.webp`;
      const file = new File([imageBlob], filename, { type: 'image/webp' });
      return await uploadToStorage(file, { folder: 'articles' });
    } catch (error) {
      console.error('Error uploading to R2:', error);
      throw error;
    }
  },

  /**
   * PLACEHOLDER: Generate AI image blob
   * Replace with actual MidJourney API integration
   */
  async generateAIImagePlaceholder(focusKeyword, location) {
    // Create a canvas with placeholder content
    const canvas = document.createElement('canvas');
    canvas.width = 1200; // 3:2 aspect ratio
    canvas.height = 800;
    
    const ctx = canvas?.getContext('2d');
    
    // Fill with gradient background
    const gradient = ctx?.createLinearGradient(0, 0, 1200, 800);
    gradient?.addColorStop(0, '#f093fb');
    gradient?.addColorStop(1, '#f5576c');
    ctx.fillStyle = gradient;
    ctx?.fillRect(0, 0, 1200, 800);
    
    // Add text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx?.fillText(focusKeyword, 600, 400);
    
    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], `${location}-${Date.now()}.jpg`, { type: 'image/jpeg' });
        resolve(file);
      }, 'image/jpeg', 0.95);
    });
  },

  /**
   * Create WordPress post with full content and image generation
   * Now includes automatic image generation for queue-processed articles
   */
  async createWordPressPost(siteId, postData, wordpressPostRowId, options = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: site, error: siteError } = await supabase?.from('wp_sites')?.select('*')?.eq('id', siteId)?.eq('user_id', user?.id)?.single();
      if (siteError) throw siteError;
      
      if (!site) throw new Error('Site not found');

      // Resolve external featured image URL (storage.gymplix.com — no WP media upload)
      let external_featured_image = postData?.external_featured_image || postData?.featured_image_url || null;

      if (options?.generateImages && !external_featured_image) {
        console.log('🖼️ Generating AI images for queue-processed article...');

        try {
          const uploadedImages = await this.generateAndUploadArticleImages(
            siteId,
            postData?.focusKeyword || postData?.title,
            ['hero']
          );

          if (uploadedImages?.length > 0) {
            external_featured_image = uploadedImages?.[0]?.storage_url || uploadedImages?.[0]?.wp_url;
            console.log('✅ Featured image on storage.gymplix.com:', external_featured_image);
          }
        } catch (imageError) {
          console.error('⚠️ Image generation failed, continuing without images:', imageError);
        }
      }

      // Use the Gymplix plugin endpoint so external_featured_image is stored as post meta.
      // We no longer call /wp-json/wp/v2/posts directly with featured_media.
      const response = await fetch(`${site?.site_url}/wp-json/gymplix/v1/create-article`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${site?.wp_username}:${site?.wp_app_password}`)}`,
        },
        body: JSON.stringify({
          title: postData?.title,
          content: postData?.content,
          status: 'draft',
          external_featured_image: external_featured_image,
          slug: postData?.slug,
          excerpt: postData?.excerpt,
          categories: postData?.categories || [],
        }),
      });

      if (!response?.ok) {
  throw new Error(`Post creation failed: ${response.statusText}`);
}

const wpPost = await response?.json();

/**
 * ✅ UPDATE AUTOMATION STATUS
 */
// ✅ UPDATE AUTOMATION STATUS — KEEP AS DRAFT
if (wordpressPostRowId) {
  await supabase?.from('wordpress_posts')?.update({
      status: 'draft', // ✅ FIX
      wp_post_id: wpPost?.id,
      published_url: null, // draft has no URL yet
      updated_at: new Date()?.toISOString()
    })?.eq('id', wordpressPostRowId);
}


return wpPost;

    } catch (error) {
      console.error('Error creating WordPress post:', error);

  if (wordpressPostRowId) {
    await supabase?.from('wordpress_posts')?.update({
        status: 'failed',
        updated_at: new Date()?.toISOString()
      })?.eq('id', wordpressPostRowId);
  }
      throw error;
    }
  },

  /**
   * Save article to WordPress with Rank Math SEO data
   */
  async saveArticleToWordPress(siteId, articleData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate focus keyword is present
      if (!articleData?.focusKeyword?.trim()) {
        throw new Error('❌ Focus keyword is REQUIRED. Cannot save article without focus keyword.');
      }

      // Get site details
      const { data: site, error: siteError } = await supabase
        ?.from('wp_sites')
        ?.select('*')
        ?.eq('id', siteId)
        ?.eq('user_id', user?.id)
        ?.single();

      if (siteError) throw siteError;

      const base = site?.site_url?.replace(/\/$/, '');
      let token = site?.wp_token;

      if (!token) {
        throw new Error('WordPress authentication token not found. Please reconnect your site.');
      }

      // Generate Rank Math SEO data
      const openaiService = await import('./openaiService');
      
      // Generate SEO title (50-60 chars with number)
      const seoTitle = articleData?.seoTitle || 
        (await openaiService?.generateRankMathSEOTitle(articleData?.focusKeyword, articleData?.title));
      
      // Generate meta description (140-160 chars)
      const metaDescription = articleData?.metaDescription || 
        (await openaiService?.generateRankMathMetaDescription(
          articleData?.focusKeyword, 
          articleData?.title, 
          articleData?.shortDescription
        ));
      
      // Get existing slugs to avoid duplicates
      const existingSlugs = await this.getExistingPermalinks(siteId);
      
      // Generate permalink with auto-increment
      const permalink = articleData?.permalink || 
        openaiService?.generateRankMathPermalink(articleData?.focusKeyword, existingSlugs);
      
      // Ensure keyword density (15+ occurrences)
      const densityResult = await openaiService?.ensureKeywordDensity(
        articleData?.content, 
        articleData?.focusKeyword, 
        15
      );
      
      const finalContent = densityResult?.content || articleData?.content;
      const keywordCount = densityResult?.keywordCount || 0;
      
      if (keywordCount < 15) {
        console.warn(`⚠️ Warning: Focus keyword appears only ${keywordCount} times (target: 15+)`);
      }

      // Prepare WordPress post data with Rank Math meta
      // external_featured_image is a URL on storage.gymplix.com — no WP media upload.
      const postData = {
        title: articleData?.title,
        content: finalContent,
        status: articleData?.status || 'draft',
        categories: articleData?.categoryId ? [articleData?.categoryId] : [],
        external_featured_image: articleData?.featuredImageUrl || articleData?.imageUrl || null,
        slug: permalink,
        meta: {
          // Rank Math SEO fields
          rank_math_title: seoTitle,
          rank_math_description: metaDescription,
          rank_math_focus_keyword: articleData?.focusKeyword,
          rank_math_permalink: permalink,
          // Additional SEO data
          _keyword_density: keywordCount,
          _seo_optimized: true
        }
      };

      // Save to WordPress via GymPlix connector
      const response = await fetch(`${base}/wp-json/gymplix/v1/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      const result = await response?.json();

      if (!result?.success) {
        throw new Error(result?.message || 'Failed to save article to WordPress');
      }

      // Save permalink to database for future duplicate checking
      await this.savePermalink(siteId, permalink);

      return {
        success: true,
        postId: result?.post_id,
        postUrl: result?.post_url,
        seoData: {
          seoTitle,
          metaDescription,
          permalink,
          keywordCount
        }
      };
    } catch (error) {
      console.error('Error saving to WordPress:', error);
      throw error;
    }
  },

  /**
   * Get existing permalinks to avoid duplicates
   */
  async getExistingPermalinks(siteId) {
    try {
      const { data, error } = await supabase
        ?.from('wp_permalinks')
        ?.select('slug')
        ?.eq('site_id', siteId);

      if (error) throw error;

      return data?.map(p => p?.slug) || [];
    } catch (error) {
      console.error('Error fetching permalinks:', error);
      return [];
    }
  },

  /**
   * Save permalink to database
   */
  async savePermalink(siteId, slug) {
    try {
      const { error } = await supabase
        ?.from('wp_permalinks')
        ?.insert({
          site_id: siteId,
          slug: slug,
          created_at: new Date()?.toISOString()
        });

      if (error && error?.code !== '23505') { // Ignore duplicate key errors
        throw error;
      }
    } catch (error) {
      console.error('Error saving permalink:', error);
    }
  },

  /**
   * Fetch WordPress categories for a site - NEW
   */
  async fetchWordPressCategories(siteId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: site, error: siteError } = await supabase?.from('wp_sites')?.select('*')?.eq('id', siteId)?.eq('user_id', user?.id)?.single();
      if (siteError) throw siteError;
      
      if (!site) throw new Error('Site not found');

      const response = await fetch(`${site?.site_url}/wp-json/wp/v2/categories?per_page=100`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${site?.wp_username}:${site?.wp_app_password}`)}`,
        },
      });

      if (!response?.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }

      return await response?.json();
    } catch (error) {
      console.error('Error fetching WordPress categories:', error);
      return [];
    }
  },

  /**
   * Update RankMath meta fields
   * Uses multiple fallback methods to ensure SEO data is written
   */
  async updateRankMathMeta(siteId, postId, seoData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data: site, error: siteError } = await supabase?.from('wp_sites')?.select('*')?.eq('id', siteId)?.eq('user_id', user?.id)?.single();
      if (siteError) throw siteError;
      
      if (!site) throw new Error('Site not found');

      const authHeader = `Basic ${btoa(`${site?.wp_username}:${site?.wp_app_password}`)}`;
      const baseUrl = site?.site_url?.replace(/\/$/, '');
      let success = false;

      // Method 1: Try Gymplix custom endpoint (most reliable)
      try {
        const response = await fetch(`${baseUrl}/wp-json/gymplix/v1/rankmath/update`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authHeader,
          },
          body: JSON.stringify({
            post_id: postId,
            seo_title: seoData?.seo_title || '',
            meta_description: seoData?.meta_description || '',
            focus_keyword: seoData?.focus_keyword || '',
            permalink: seoData?.permalink || '',
          }),
        });

        if (response?.ok) {
          const result = await response.json();
          if (result?.success) {
            console.log('RankMath meta updated via Gymplix endpoint:', result);
            success = true;
          }
        }
      } catch (e) {
        console.warn('Gymplix endpoint not available:', e?.message);
      }

      // Method 2: Try RankMath native REST API endpoint
      if (!success) {
        try {
          const response = await fetch(`${baseUrl}/wp-json/rankmath/v1/updateMeta`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
            body: JSON.stringify({
              objectID: postId,
              objectType: 'post',
              meta: {
                rank_math_title: seoData?.seo_title,
                rank_math_description: seoData?.meta_description,
                rank_math_focus_keyword: seoData?.focus_keyword,
              },
            }),
          });

          if (response?.ok) {
            console.log('RankMath meta updated via native endpoint');
            success = true;
          }
        } catch (e) {
          console.warn('RankMath native API error:', e?.message);
        }
      }

      // Method 3: Try WordPress REST API with registered meta fields
      if (!success) {
        try {
          const response = await fetch(`${baseUrl}/wp-json/wp/v2/posts/${postId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
            body: JSON.stringify({
              meta: {
                rank_math_title: seoData?.seo_title || '',
                rank_math_description: seoData?.meta_description || '',
                rank_math_focus_keyword: seoData?.focus_keyword || '',
                rank_math_robots: ['index', 'follow'],
              }
            }),
          });

          if (response?.ok) {
            console.log('RankMath meta updated via WP REST API');
            success = true;
          }
        } catch (e) {
          console.warn('WP REST API error:', e?.message);
        }
      }

      // Verify the meta was actually saved
      if (success) {
        try {
          const verifyResponse = await fetch(`${baseUrl}/wp-json/wp/v2/posts/${postId}?context=edit`, {
            headers: { Authorization: authHeader },
          });
          
          if (verifyResponse?.ok) {
            const postData = await verifyResponse.json();
            const meta = postData?.meta || {};
            
            const hasRankMath = meta?.rank_math_title || meta?.rank_math_description || meta?.rank_math_focus_keyword;
            
            if (!hasRankMath) {
              console.warn('RankMath meta not visible in REST response - plugin may need Gymplix bridge installed');
            } else {
              console.log('RankMath meta verified:', {
                title: meta?.rank_math_title,
                description: meta?.rank_math_description?.substring(0, 50) + '...',
                keyword: meta?.rank_math_focus_keyword,
              });
            }
          }
        } catch (e) {
          // Verification is optional, don't fail on error
        }
      }

      return success;
    } catch (error) {
      console.error('Error updating RankMath meta:', error);
      return false;
    }
  },

  /**
   * Get automation-generated posts with filters
   */
  async getAutomationPosts({ siteId, status, fromDate, toDate, search } = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('wordpress_posts')
        ?.select(`
          id,
          site_id,
          wp_post_id,
          title,
          status,
          published_url,
          created_at,
          wp_sites!inner(site_name, site_url, user_id)
        `)
        ?.eq('wp_sites.user_id', user?.id)
        ?.order('created_at', { ascending: false });

      // Filter by site if provided
      if (siteId) {
        query = query?.eq('site_id', siteId);
      }

      // Filter by status if provided
      if (status && status !== 'all') {
        query = query?.eq('status', status);
      }

      // Filter by date range if provided
      if (fromDate) {
        query = query?.gte('created_at', fromDate);
      }
      if (toDate) {
        query = query?.lte('created_at', toDate);
      }

      // Filter by search term if provided
      if (search && search?.trim()) {
        query = query?.ilike('title', `%${search?.trim()}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(post => ({
        id: post?.id,
        siteId: post?.site_id,
        wpPostId: post?.wp_post_id,
        title: post?.title,
        status: post?.status,
        publishedUrl: post?.published_url,
        createdAt: post?.created_at,
        siteName: post?.wp_sites?.site_name,
        siteUrl: post?.wp_sites?.site_url
      })) || [];
    } catch (error) {
      console.error('Error fetching automation posts:', error);
      throw error;
    }
  },

  /**
   * Get post counts grouped by status
   */
  async getAutomationPostCounts({ siteId, fromDate, toDate, search } = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('wordpress_posts')
        ?.select('status, wp_sites!inner(user_id)')
        ?.eq('wp_sites.user_id', user?.id);

      // Filter by site if provided
      if (siteId) {
        query = query?.eq('site_id', siteId);
      }

      // Filter by date range if provided
      if (fromDate) {
        query = query?.gte('created_at', fromDate);
      }
      if (toDate) {
        query = query?.lte('created_at', toDate);
      }

      // Filter by search term if provided
      if (search && search?.trim()) {
        query = query?.ilike('title', `%${search?.trim()}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate counts by status
      const counts = {
        all: data?.length || 0,
        published: data?.filter(p => p?.status === 'published')?.length || 0,
        draft: data?.filter(p => p?.status === 'draft')?.length || 0,
        scheduled: data?.filter(p => p?.status === 'scheduled')?.length || 0
      };

      return counts;
    } catch (error) {
      console.error('Error fetching post counts:', error);
      throw error;
    }
  },

  /**
   * Fetch website title from URL for auto-generation
   * Updated to avoid CORS issues by using domain parsing only
   */
  async fetchWebsiteTitle(websiteUrl) {
    // Note: Direct browser fetching causes CORS errors
    // Using domain parsing as a reliable fallback
    try {
      const url = new URL(websiteUrl);
      let domain = url?.hostname?.replace(/^www\./, '');
      
      // Generate a nice title from domain
      let title = domain
        ?.split('.')
        ?.slice(0, -1) // Remove TLD (.com, .org, etc.)
        ?.join(' ')
        ?.split(/[-_]/)
        ?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())
        ?.join(' ');
      
      return title || domain;
    } catch (error) {
      console.error('Error parsing website URL:', error);
      return null;
    }
  },

  /**
   * Generate website name from URL
   */
  generateWebsiteNameFromUrl(websiteUrl) {
    try {
      const url = new URL(websiteUrl);
      let domain = url?.hostname?.replace(/^www\./, '');
      
      // Capitalize first letter
      return domain?.charAt(0)?.toUpperCase() + domain?.slice(1);
    } catch (error) {
      return 'Website';
    }
  },

  /**
   * Normalize URL for duplicate checking
   */
  normalizeUrl(url) {
    try {
      const urlObj = new URL(url);
      // Remove www., trailing slash, and convert to lowercase
      let normalized = urlObj?.hostname?.replace(/^www\./, '')?.toLowerCase();
      return normalized;
    } catch (error) {
      return url?.toLowerCase();
    }
  },

  /**
   * Add a new copy blogs source with auto-generated name
   */
  async addCopyBlogsSource(websiteUrl, profileId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate URL
      if (!websiteUrl || !websiteUrl?.trim()) {
        throw new Error('Website URL is required');
      }

      // Normalize URL for duplicate check using wordpressService reference
      const normalizedUrl = wordpressService?.normalizeUrl(websiteUrl);

      // Check for duplicates — scoped to the same profile only, since the
      // same external blog can be added independently under each profile.
      let dupQuery = supabase
        ?.from('copy_blogs_sources')
        ?.select('website_url')
        ?.eq('user_id', user?.id);
      if (profileId) dupQuery = dupQuery?.eq('profile_id', profileId);
      const { data: existingSources } = await dupQuery;

      const isDuplicate = existingSources?.some(source =>
        wordpressService?.normalizeUrl(source?.website_url) === normalizedUrl
      );

      if (isDuplicate) {
        throw new Error('This website is already added.');
      }

      // Try to fetch website title
      let websiteName = await wordpressService?.fetchWebsiteTitle(websiteUrl);

      // Fallback to domain name if title fetch fails
      if (!websiteName) {
        websiteName = wordpressService?.generateWebsiteNameFromUrl(websiteUrl);
      }

      // Insert into database
      const { data, error } = await supabase
        ?.from('copy_blogs_sources')
        ?.insert({
          user_id: user?.id,
          profile_id: profileId || null,
          website_name: websiteName,
          website_url: websiteUrl,
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        profileId: data?.profile_id,
        websiteName: data?.website_name,
        websiteUrl: data?.website_url,
        favicon: data?.favicon,
        lastScrapedAt: data?.last_scraped_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
      };
    } catch (error) {
      console.error('Error adding copy blogs source:', error);
      throw error;
    }
  },

  /**
   * Get all copy blogs sources for the current user, scoped to a language profile
   */
  async getCopyBlogsSources(profileId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('copy_blogs_sources')
        ?.select('*')
        ?.eq('user_id', user?.id);
      if (profileId) query = query?.eq('profile_id', profileId);
      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(source => ({
        id: source?.id,
        userId: source?.user_id,
        profileId: source?.profile_id,
        websiteName: source?.website_name,
        websiteUrl: source?.website_url,
        favicon: source?.favicon,
        lastScrapedAt: source?.last_scraped_at,
        createdAt: source?.created_at,
        updatedAt: source?.updated_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching copy blogs sources:', error);
      throw error;
    }
  },

  /**
   * Delete a copy blogs source
   */
  async deleteCopyBlogsSource(sourceId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('copy_blogs_sources')
        ?.delete()
        ?.eq('id', sourceId)
        ?.eq('user_id', user?.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting copy blogs source:', error);
      throw error;
    }
  },

  /**
   * Get articles for a specific source with pagination - OPTIMIZED for speed
   * Fetches only essential fields: title, featured image, publish date, article URL
   */
  async getCopyBlogsArticles(sourceId, { limit = 10, offset = 0 } = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // ✅ OPTIMIZED: Only select essential fields for fast initial load
      let query = supabase
        ?.from('copy_blogs_articles')
        ?.select('id, title, article_url, featured_image_url, publish_date, is_added_to_automation')
        ?.eq('source_id', sourceId)
        ?.eq('user_id', user?.id)
        ?.order('publish_date', { ascending: false })
        ?.range(offset, offset + limit - 1);

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(article => ({
        id: article?.id,
        title: article?.title,
        articleUrl: article?.article_url,
        featuredImageUrl: article?.featured_image_url,
        publishDate: article?.publish_date,
        isAddedToAutomation: article?.is_added_to_automation,
      })) || [];
    } catch (error) {
      console.error('Error fetching copy blogs articles:', error);
      throw error;
    }
  },

  /**
   * Get total count of articles for a source (for pagination)
   */
  async getCopyBlogsArticlesCount(sourceId, { dateFilter = null } = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('copy_blogs_articles')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('source_id', sourceId)
        ?.eq('user_id', user?.id);

      // Apply same date filter logic
      if (dateFilter) {
        const now = new Date();
        let startDate;

        switch (dateFilter) {
          case 'last_24_hours':
            startDate = new Date(now?.getTime() - 24 * 60 * 60 * 1000);
            break;
          case 'last_3_days':
            startDate = new Date(now?.setDate(now?.getDate() - 3));
            break;
          case 'last_7_days':
            startDate = new Date(now?.setDate(now?.getDate() - 7));
            break;
          case 'last_30_days':
            startDate = new Date(now?.setDate(now?.getDate() - 30));
            break;
          case 'this_year':
            startDate = new Date(now?.getFullYear(), 0, 1);
            break;
          default:
            break;
        }

        if (startDate) {
          query = query?.gte('publish_date', startDate?.toISOString());
        }
      }

      const { count, error } = await query;

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Error fetching articles count:', error);
      throw error;
    }
  },

  /**
   * Save scraped articles to database
   */
  async saveCopyBlogsArticles(sourceId, articles) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Articles always inherit their source's profile — never re-derived
    // from whatever profile happens to be active when scraping runs.
    const { data: sourceRow } = await supabase
      ?.from('copy_blogs_sources')
      ?.select('profile_id')
      ?.eq('id', sourceId)
      ?.eq('user_id', user?.id)
      ?.single();
    const sourceProfileId = sourceRow?.profile_id || null;

    const articlesToInsert = articles?.map(article => ({
      source_id: sourceId,
      user_id: user?.id,
      profile_id: sourceProfileId,
      title: article?.title || 'Untitled',
      article_url: article?.articleUrl,
      excerpt: article?.excerpt || '',
      featured_image_url: article?.featuredImageUrl || '',
      focus_keyword: article?.focusKeyword || '',
      category: article?.category || '',
      meta_description: article?.metaDescription || article?.excerpt || '',
      publish_date: article?.publishDate
        ? new Date(article?.publishDate)?.toISOString()
        : new Date()?.toISOString(),
    }));

    const { error } = await supabase?.from('copy_blogs_articles')?.upsert(articlesToInsert, {
        onConflict: 'source_id,article_url',
      });

    if (error) throw error;

    await supabase?.from('copy_blogs_sources')?.update({ last_scraped_at: new Date()?.toISOString() })?.eq('id', sourceId)?.eq('user_id', user?.id);

  } catch (error) {
    console.error('Error saving copy blogs articles:', error);
    throw error;
  }
},

  /**
   * Mark article as added to automation
   */
  async markArticleAddedToAutomation(articleId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('copy_blogs_articles')
        ?.update({ is_added_to_automation: true })
        ?.eq('id', articleId)
        ?.eq('user_id', user?.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking article as added:', error);
      throw error;
    }
  },

  /**
   * Create or update a draft article in the database
   */
  async saveDraftArticle(articleData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // CRITICAL FIX: focus_keyword is NOT NULL - must have a value
      const focusKeyword = articleData?.focusKeyword || null;
      
      // Only save if we have at least a focus keyword (required field)
      if (!focusKeyword) {
        throw new Error('Focus keyword is required to save draft');
      }

      const articleToSave = {
        user_id: user?.id,
        site_id: articleData?.siteId || null,
        profile_id: articleData?.profileId || null,
        language: articleData?.language || 'en',
        focus_keyword: focusKeyword, // Now guaranteed to have a value
        title: articleData?.title || null,
        short_description: articleData?.shortDescription || null,
        seo_title: articleData?.seoTitle || null,
        meta_description: articleData?.metaDescription || null,
        status: 'draft',
        prompt_id: articleData?.promptId || null,
        enable_recipe_card: articleData?.enableRecipeCard || false
      };

      if (articleData?.id) {
        // FIX: First verify the article exists and belongs to the user
        const { data: existingArticle, error: checkError } = await supabase
          ?.from('wp_articles')
          ?.select('id, user_id')
          ?.eq('id', articleData?.id)
          ?.single();

        if (checkError || !existingArticle) {
          console.warn('Article not found, creating new one instead');
          // Article doesn't exist, create a new one instead
          const { data: newData, error: insertError } = await supabase
            ?.from('wp_articles')
            ?.insert([articleToSave])
            ?.select();

          if (insertError) throw insertError;
          
          if (!newData || newData?.length === 0) {
            throw new Error('Failed to create draft article - no rows returned');
          }
          
          return newData?.[0];
        }

        // Verify ownership
        if (existingArticle?.user_id !== user?.id) {
          throw new Error('You do not have permission to update this article');
        }

        // Update existing draft
        const { data, error } = await supabase
          ?.from('wp_articles')
          ?.update(articleToSave)
          ?.eq('id', articleData?.id)
          ?.eq('user_id', user?.id)
          ?.select();

        if (error) throw error;
        
        // Return first item if data exists, otherwise throw error
        if (!data || data?.length === 0) {
          throw new Error('Failed to update draft article - article may have been deleted or modified');
        }
        
        return data?.[0];
      } else {
        // Insert new draft
        const { data, error } = await supabase
          ?.from('wp_articles')
          ?.insert([articleToSave])
          ?.select();

        if (error) throw error;
        
        // Return first item if data exists, otherwise throw error
        if (!data || data?.length === 0) {
          throw new Error('Failed to create draft article - no rows returned');
        }
        
        return data?.[0];
      }
    } catch (error) {
      console.error('Error saving draft article:', error);
      throw error;
    }
  },

  /**
   * Get all draft articles for the current user
   */
  async getDraftArticles(profileId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('wp_articles')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('status', 'draft');
      if (profileId) query = query?.eq('profile_id', profileId);
      const { data, error } = await query?.order('updated_at', { ascending: false });

      if (error) throw error;

      return data?.map(article => wordpressService?.convertArticleFromDB(article)) || [];
    } catch (error) {
      console.error('Error fetching draft articles:', error);
      throw error;
    }
  },

  /**
   * Get a single draft article by ID
   */
  async getDraftArticleById(articleId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('wp_articles')
        ?.select('*')
        ?.eq('id', articleId)
        ?.eq('user_id', user?.id)
        ?.eq('status', 'draft')
        ?.single();

      if (error) throw error;
      return wordpressService?.convertArticleFromDB(data);
    } catch (error) {
      console.error('Error fetching draft article:', error);
      throw error;
    }
  },

  /**
   * Delete a draft article
   */
  async deleteDraftArticle(articleId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('wp_articles')
        ?.delete()
        ?.eq('id', articleId)
        ?.eq('user_id', user?.id)
        ?.eq('status', 'draft');

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting draft article:', error);
      throw error;
    }
  },

  /**
   * Helper function to convert database article to camelCase
   */
  convertArticleFromDB(article) {
    return {
      id: article?.id,
      userId: article?.user_id,
      siteId: article?.site_id,
      focusKeyword: article?.focus_keyword,
      title: article?.title,
      shortDescription: article?.short_description,
      status: article?.status,
      promptId: article?.prompt_id,
      enableRecipeCard: article?.enable_recipe_card,
      seoTitle: article?.seo_title,
      permalink: article?.permalink,
      metaDescription: article?.meta_description,
      secondaryKeywords: article?.secondary_keywords,
      tags: article?.tags,
      wpPostId: article?.wp_post_id,
      wpPostUrl: article?.wp_post_url,
      generatedContent: article?.generated_content,
      errorMessage: article?.error_message,
      createdAt: article?.created_at,
      updatedAt: article?.updated_at,
      publishedAt: article?.published_at,
    };
  },

  /**
   * Get all prompts for the current user
   */
  async getAllPrompts(profileId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('wp_prompts')
        ?.select('*')
        ?.eq('user_id', user?.id);
      if (profileId) query = query?.eq('profile_id', profileId);
      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(prompt => ({
        id: prompt?.id,
        name: prompt?.name,
        content: prompt?.content,
        promptType: prompt?.prompt_type,
        isDefault: prompt?.is_default,
        userId: prompt?.user_id,
        profileId: prompt?.profile_id,
        createdAt: prompt?.created_at,
        updatedAt: prompt?.updated_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching prompts:', error);
      throw error;
    }
  },

  /**
   * Create a new prompt
   */
  async createPrompt(promptData, profileId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('wp_prompts')
        ?.insert({
          user_id: user?.id,
          profile_id: profileId || null,
          name: promptData?.name,
          content: promptData?.content,
          prompt_type: promptData?.promptType,
          is_default: false,
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        content: data?.content,
        promptType: data?.prompt_type,
        isDefault: data?.is_default,
        userId: data?.user_id,
        profileId: data?.profile_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
      };
    } catch (error) {
      console.error('Error creating prompt:', error);
      throw error;
    }
  },

  /**
   * Update an existing prompt
   */
  async updatePrompt(promptId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData = {};
      if (updates?.name !== undefined) updateData.name = updates?.name;
      if (updates?.content !== undefined) updateData.content = updates?.content;
      if (updates?.promptType !== undefined) updateData.prompt_type = updates?.promptType;

      const { data, error } = await supabase
        ?.from('wp_prompts')
        ?.update(updateData)
        ?.eq('id', promptId)
        ?.eq('user_id', user?.id)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        content: data?.content,
        promptType: data?.prompt_type,
        isDefault: data?.is_default,
        userId: data?.user_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
      };
    } catch (error) {
      console.error('Error updating prompt:', error);
      throw error;
    }
  },

  /**
   * Delete a prompt
   */
  async deletePrompt(promptId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('wp_prompts')
        ?.delete()
        ?.eq('id', promptId)
        ?.eq('user_id', user?.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw error;
    }
  },

  /**
   * Get a single prompt by ID
   */
  async getPromptById(promptId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('wp_prompts')
        ?.select('*')
        ?.eq('id', promptId)
        ?.eq('user_id', user?.id)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        content: data?.content,
        promptType: data?.prompt_type,
        isDefault: data?.is_default,
        userId: data?.user_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
      };
    } catch (error) {
      console.error('Error fetching prompt:', error);
      throw error;
    }
  }
};

// Export individual functions for easier imports
export const getSites = wordpressService?.getAllSites;
export const uploadImageToWordPress = wordpressService?.uploadImageToWordPress;
export const createSite = wordpressService?.createSite;
export const updateSite = wordpressService?.updateSite;
export const deleteSite = wordpressService?.deleteSite;
export const testGymPlixConnection = wordpressService?.testGymPlixConnection;
export const fetchGymPlixCategories = wordpressService?.fetchGymPlixCategories;
export const createWordPressPost = wordpressService?.createWordPressPost;
export const updateRankMathMeta = wordpressService?.updateRankMathMeta;
export const getAutomationPosts = wordpressService?.getAutomationPosts;
export const getAutomationPostCounts = wordpressService?.getAutomationPostCounts;
export const getCopyBlogsSources = wordpressService?.getCopyBlogsSources;
export const addCopyBlogsSource = wordpressService?.addCopyBlogsSource;
export const deleteCopyBlogsSource = wordpressService?.deleteCopyBlogsSource;
export const getCopyBlogsArticles = wordpressService?.getCopyBlogsArticles;
export const getCopyBlogsArticlesCount = wordpressService?.getCopyBlogsArticlesCount;
export const saveCopyBlogsArticles = wordpressService?.saveCopyBlogsArticles;
export const markArticleAddedToAutomation = wordpressService?.markArticleAddedToAutomation;
export const getAllPrompts = wordpressService?.getAllPrompts;
export const createPrompt = wordpressService?.createPrompt;
export const updatePrompt = wordpressService?.updatePrompt;
export const deletePrompt = wordpressService?.deletePrompt;
export const getPromptById = wordpressService?.getPromptById;
export const saveDraftArticle = wordpressService?.saveDraftArticle;
export const getDraftArticles = wordpressService?.getDraftArticles;
export const getDraftArticleById = wordpressService?.getDraftArticleById;
export const deleteDraftArticle = wordpressService?.deleteDraftArticle;
export const fetchWordPressCategories = wordpressService?.fetchWordPressCategories;
export const saveArticleToWordPress = wordpressService?.saveArticleToWordPress;
